/**
 * Angular Component BMFileUploader
 * Drag & Drop File uploader
 */
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription } from 'rxjs';

import { UploadEventsService } from '../upload-events-service/upload-events-service';
import {
  FileStatus,
  FileUploaderFile,
  FileUploaderUploadEvent,
  ValidDataTypes,
} from '../uploaders.interfaces';

@Component({
  selector: 'bm-file-upload',
  templateUrl: 'bm-file.uploader.component.html',
  styleUrls: ['bm-file.uploader.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // tslint:disable-next-line:no-forward-ref
      useExisting: forwardRef(() => BMFileUploaderComponent),
      multi: true,
    },
    UploadEventsService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // tslint:disable-next-line:use-component-view-encapsulation
  encapsulation: ViewEncapsulation.None,
})
export class BMFileUploaderComponent
  implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() public accept!: string;
  @Input() public dataType!: ValidDataTypes;
  @Input() public disabled!: boolean;
  @Input() public set multiple(v: boolean) {
    this.multipleInt = v ? 1 : 0;
  }
  public get multiple() {
    return this.multipleInt === 1;
  }

  @Output() public abort = new EventEmitter<FileUploaderUploadEvent>();
  @Output() public done = new EventEmitter<FileUploaderUploadEvent>();
  @Output() public empty = new EventEmitter<FileUploaderUploadEvent>();
  @Output() public error = new EventEmitter<FileUploaderUploadEvent>();
  @Output() public loading = new EventEmitter<FileUploaderUploadEvent>();

  public multipleInt = 0;

  private _subs: Subscription = Subscription.EMPTY;

  private _onChangeCb!: (_: FileUploaderFile[]) => void;
  private _onTouchedCb!: () => void;

  constructor(private _uploadEventsService: UploadEventsService) {}

  public ngOnInit() {
    this._uploadEventsService.events$.subscribe({
      // tslint:disable-next-line:cyclomatic-complexity
      next: event => {
        switch (event.status) {
          case FileStatus.ABORT:
            this.abort.emit(event);

            if (this._onChangeCb) {
              this._onChangeCb([]);
            }

            break;
          case FileStatus.DONE:
            this.done.emit(event);

            if (this._onChangeCb) {
              this._onChangeCb(event.files.map(e => e.file));
            }

            break;
          case FileStatus.EMPTY:
            this.empty.emit(event);

            if (this._onChangeCb) {
              this._onChangeCb([]);
            }

            break;
          case FileStatus.ERROR:
            this.error.emit(event);

            if (this._onChangeCb) {
              this._onChangeCb([]);
            }

            break;
          case FileStatus.LOADING:
            this.loading.emit(event);

            if (this._onTouchedCb) {
              this._onTouchedCb();
            }
            break;
          default:
        }
      },
    });
  }

  public ngOnDestroy() {
    this._subs.unsubscribe();
  }

  public onFocusin() {
    if (this._onTouchedCb) {
      this._onTouchedCb();
    }
  }

  public writeValue(val: FileList | unknown) {
    if (val instanceof FileList) {
      this._uploadEventsService.readFiles(val, this.dataType);
    } else {
      this._uploadEventsService.readFiles(null, this.dataType);
    }
  }

  public registerOnChange(fn: (_: FileUploaderFile[]) => void) {
    this._onChangeCb = fn;
  }

  public registerOnTouched(fn: () => void) {
    this._onTouchedCb = fn;
  }

  public setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }
}
